import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplate,
  FormTemplateSection,
} from 'data/template/template.types';
import * as types from 'data/xtp/xtp.types';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import SectionLabel from 'components/SectionLabel';
import './xtp-form.scss';

type Props = {
  template: FormTemplate;
  data: Partial<{ xtp: types.Xtp, xtpOptions: types.XtpOptions }>;
  i18nKeyPrefix?: string;
  isEditing?: boolean;
  onFieldUpdate?: (
    param: FieldUpdateHandlerParam,
    formSection?: 'main' | 'xtpOptions'
  ) => void;
};

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => { main: types.Xtp, xtpOptions: types.XtpOptions };
};

export const XTP_I18N_KEY =
  'xtp.form';

export const XtpForm = forwardRef<Ref, Props>(
  (
    {
      template,
      data,
      i18nKeyPrefix = XTP_I18N_KEY,
      onFieldUpdate = () => null,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const mainRef = useRef<Form>(null);
    const xtpOptionsRef = useRef<Form>(null);

    const customFieldComponentMap = {
      privateKey: SectionLabel,
      certificate: SectionLabel,
    };

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isMainValid =
        mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
      const isXtpOptionsValid =
        xtpOptionsRef.current?.isValid(forceValidation, forceTouched) ?? true;
      return isMainValid && isXtpOptionsValid;
    };

    const getValues = () => {
      const formValues = {
        main: mainRef.current?.getValues() as types.Xtp,
        xtpOptions: xtpOptionsRef.current?.getValues() as types.XtpOptions,
      };
      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues,
    }));

    const handleMainFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'main');

    const handleXtpOptionsUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'xtpOptions');

    return (
      <div
        className="xtp-form"
        data-testid="XtpForm"
      >
        <div data-testid="XtpFormMain">
          <FormLayout>
            <Form
              ref={mainRef}
              name={template.formId ?? template.key}
              defaults={data.xtp}
              fields={(template.main as FormTemplateSection).fields}
              layout={
                (template.main as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={handleMainFieldUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />
          </FormLayout>
        </div>
        <div data-testid="FormXtpOptions">
          <h3 className="xtp-form-subtitle">{t('xtp.form.optionsSection')}</h3>
          <FormLayout>
            <Form
              ref={xtpOptionsRef}
              name={template.formId ?? template.key}
              defaults={data.xtpOptions}
              fields={(template.xtpOptions as FormTemplateSection).fields}
              layout={
                (template.xtpOptions as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={handleXtpOptionsUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />
          </FormLayout>
        </div>
      </div>
    );
  },
);

export default XtpForm;
